/**
 * Cart drawer — owns open/close and the two controls that are not plain
 * `/cart/change` links, and re-renders itself from the section HTML that
 * cart.js bundles into every mutation ('cart:change').
 *
 * Open/close is a native <dialog>. showModal() gives the top layer, the scrim
 * as ::backdrop, Escape, focus moved into the panel, focus returned to the cart
 * icon on close, and inertness for the page behind — none of which is
 * reimplemented here. Every dismissal (the ✕, the scrim, Escape, Continue
 * shopping) ends at the dialog's own `close` event, so the scroll lock is
 * released in exactly one place.
 *
 * Mutations go through window.Cart in assets/cart.js. The remove links are real
 * hrefs its delegation already intercepts; the quantity select is handled
 * there too; only the plan toggle is here, because swapping a line's selling
 * plan is not something an href can express.
 *
 * @extends HTMLElement
 */

export class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.onCartChange = this.onCartChange.bind(this);
    this.onCartError = this.onCartError.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDialogClick = this.onDialogClick.bind(this);
    this.onDialogClose = this.onDialogClose.bind(this);
    this.onInnerClick = this.onInnerClick.bind(this);
    this.onSliderPointerDown = this.onSliderPointerDown.bind(this);
    this.onSliderClickCapture = this.onSliderClickCapture.bind(this);
  }

  connectedCallback() {
    document.addEventListener('cart:change', this.onCartChange);
    document.addEventListener('cart:error', this.onCartError);
    document.addEventListener('click', this.onDocumentClick);
    this.dialog?.addEventListener('click', this.onDialogClick);
    this.dialog?.addEventListener('close', this.onDialogClose);
    this.addEventListener('click', this.onInnerClick);
    this.addEventListener('pointerdown', this.onSliderPointerDown);
    this.addEventListener('click', this.onSliderClickCapture, true);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this.onCartChange);
    document.removeEventListener('cart:error', this.onCartError);
    document.removeEventListener('click', this.onDocumentClick);
    this.dialog?.removeEventListener('click', this.onDialogClick);
    this.dialog?.removeEventListener('close', this.onDialogClose);
    this.removeEventListener('click', this.onInnerClick);
    this.removeEventListener('pointerdown', this.onSliderPointerDown);
    this.removeEventListener('click', this.onSliderClickCapture, true);
    this.endSliderDrag?.();
    this.restoreSliderSnap?.();
    document.documentElement.classList.remove('cart-drawer-open');
  }

  get dialog() {
    return this.querySelector('.cart-drawer__dialog');
  }

  get content() {
    return this.querySelector('[data-cart-drawer-content]');
  }

  get heading() {
    return this.querySelector('[data-cart-drawer-title]');
  }

  get sectionId() {
    return this.closest('.shopify-section')?.id.replace('shopify-section-', '');
  }

  open() {
    const dialog = this.dialog;
    if (!dialog || dialog.open) return;
    this.opener = document.activeElement;
    dialog.showModal();
    /* showModal() focuses the first control, the close button, and Safari rings it as if tabbed to. The
       title takes the focus instead, as live focuses its drawer; Tab still reaches the close button next. */
    this.heading?.focus();
    document.documentElement.classList.add('cart-drawer-open');
  }

  close() {
    this.dialog?.close();
  }

  /* The single place every dismissal arrives at, whoever started it. */
  onDialogClose() {
    document.documentElement.classList.remove('cart-drawer-open');
    /* Browsers return focus on their own; this covers the ones that do not. */
    if (this.opener?.isConnected && document.activeElement === document.body) this.opener.focus();
    this.opener = null;
  }

  onDocumentClick(event) {
    const trigger = event.target.closest('[data-cart-trigger]');
    if (!trigger) return;
    event.preventDefault();
    this.open();
  }

  /* The dialog fills the viewport, so a click on it rather than on the panel
     landed on the scrim. */
  onDialogClick(event) {
    if (event.target === this.dialog) this.close();
  }

  onInnerClick(event) {
    if (event.target.closest('[data-cart-drawer-close]')) {
      this.close();
      return;
    }

    const plan = event.target.closest('[data-cart-plan]');
    if (plan) this.changePlan(plan);
  }

  /**
   * Swaps the line's selling plan and lets the re-render re-price it.
   * /cart/change.js only accepts `selling_plan` alongside `line`, and treats a
   * missing quantity as 1, so both are always sent.
   */
  changePlan(button) {
    if (button.getAttribute('aria-pressed') === 'true') return;

    const line = parseInt(button.dataset.line, 10);
    if (!line) return;

    const item = button.closest('.cart-drawer__item');
    const quantity = parseInt(item?.querySelector('[data-cart-quantity]')?.value, 10) || 1;
    const sellingPlan = button.dataset.sellingPlan || null;

    /* `_no_discount` is the line property the store's discount function reads; live's drawer sends it on every plan swap.
       It joins the line's own properties, as on live: `properties` replaces them all, and a 3-month line switched to
       one-time and back needs its `_supply_gift` to earn its gift again. */
    const properties = { ...window.Cart?.state?.items?.[line - 1]?.properties, _no_discount: 'true' };
    window.Cart?.change({ line, quantity, selling_plan: sellingPlan, properties }, button).catch(() => {
      /* cart.js has already dispatched cart:error; the drawer re-renders from
         the server on the next successful change, so nothing is patched here. */
    });
  }

  onCartChange(event) {
    const html = event.detail.sections?.[this.sectionId];
    if (html) this.render(html);
    // Not for an add on its way to checkout (Cart.addForCheckout): the page is leaving
    if (event.detail.action === 'add' && !event.detail.leaving) this.open();
    this.announce();
  }

  render(html) {
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer-content]');
    const current = this.content;
    if (!next || !current) return;

    const scroller = current.querySelector('[data-cart-drawer-scroll]');
    const scrollTop = scroller ? scroller.scrollTop : 0;
    const focused = this.focusTarget(document.activeElement);

    current.innerHTML = next.innerHTML;

    const nextScroller = current.querySelector('[data-cart-drawer-scroll]');
    if (nextScroller) nextScroller.scrollTop = scrollTop;

    this.restoreFocus(focused);
  }

  /**
   * A selector that will find the same control again after the content is
   * replaced. Re-rendering destroys the focused node, so without this a
   * keyboard shopper is dropped to the top of the document mid-edit.
   */
  focusTarget(el) {
    if (!el || !this.contains(el)) return null;
    if (el.matches('[data-cart-quantity]')) return `[data-cart-quantity][data-key="${el.dataset.key}"]`;
    if (el.matches('[data-cart-plan]')) {
      return `[data-cart-plan][data-line="${el.dataset.line}"][data-selling-plan="${el.dataset.sellingPlan || ''}"]`;
    }
    if (el.matches('[data-cart-drawer-close]')) return '[data-cart-drawer-close]';
    if (el.matches('[data-cart-drawer-title]')) return '[data-cart-drawer-title]';
    return null;
  }

  restoreFocus(selector) {
    if (!selector) return;
    /* The line may be gone — a removal is a re-render too — so fall back to the
       title rather than leaving focus on the discarded node. Not the close
       button, which Safari would ring. */
    const target = this.querySelector(selector) || this.heading;
    target?.focus();
  }

  /** Reads back the failure the engine reported, for anyone not watching the line. */
  onCartError(event) {
    const message = event.detail?.error?.description;
    if (!message) return;
    const status = this.querySelector('.cart-drawer__status');
    if (status) status.textContent = message;
  }

  /**
   * Lets a mouse drag the Best Sellers row, as the live drawer's Slick slider
   * does. Touch and trackpads already scroll it natively with scroll-snap, so
   * only a mouse is handled. Snap is suspended while the row follows the
   * pointer — mandatory snap would fight every pixel of the drag — and on
   * release the row eases to the nearest card, Slick's swipeToSlide.
   */
  onSliderPointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    const track = event.target.closest('[data-cart-drawer-slider]');
    if (!track || track.scrollWidth <= track.clientWidth) return;

    /* The card's own Add to cart must still take a plain click. */
    if (event.target.closest('button, select, input')) return;

    /* Stops text selection and the browser's native image and link drag. */
    event.preventDefault();

    /* A new drag overrides the previous one's settle, still in flight. */
    this.restoreSliderSnap?.();

    const startX = event.clientX;
    const startScroll = track.scrollLeft;
    this.sliderDragged = false;
    track.classList.add('cart-drawer__cards--dragging');

    const onMove = (moveEvent) => {
      const distance = moveEvent.clientX - startX;
      if (Math.abs(distance) > 5) this.sliderDragged = true;
      track.scrollLeft = startScroll - distance;
    };

    const onUp = () => {
      this.endSliderDrag();
      this.settleSlider(track);
    };

    this.endSliderDrag = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      this.endSliderDrag = null;
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }

  /** Eases to the card nearest the drop point, then hands back to scroll-snap. */
  settleSlider(track) {
    const cards = [...track.children];
    if (!cards.length) return;

    const origin = cards[0].offsetLeft;
    const maxScroll = track.scrollWidth - track.clientWidth;
    let target = 0;
    let closest = Infinity;

    for (const card of cards) {
      const left = Math.min(card.offsetLeft - origin, maxScroll);
      const gap = Math.abs(left - track.scrollLeft);
      if (gap < closest) {
        closest = gap;
        target = left;
      }
    }

    /*
      Kept on the element so the next drag can cancel it. A drop already on a
      card fires no scrollend, and a listener left waiting would restore snap
      in the middle of the following drag.
    */
    const timer = setTimeout(() => this.restoreSliderSnap?.(), 450);
    this.restoreSliderSnap = () => {
      clearTimeout(timer);
      track.removeEventListener('scrollend', this.restoreSliderSnap);
      track.classList.remove('cart-drawer__cards--dragging');
      this.restoreSliderSnap = null;
    };

    if (Math.abs(target - track.scrollLeft) < 1) {
      this.restoreSliderSnap();
      return;
    }

    track.addEventListener('scrollend', this.restoreSliderSnap);
    track.scrollTo({ left: target, behavior: 'smooth' });
  }

  /**
   * The click a drag ends in is not a click on anything. It lands on the card
   * it started over or, released past the panel's edge, on the dialog itself —
   * which is a scrim click and would close the drawer. Captured here, on an
   * ancestor of both, so it never reaches either.
   */
  onSliderClickCapture(event) {
    if (!this.sliderDragged) return;
    this.sliderDragged = false;
    event.preventDefault();
    event.stopPropagation();
  }

  announce() {
    const status = this.querySelector('.cart-drawer__status');
    if (status) status.textContent = this.dataset.updatedText || '';
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
